import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { AppViewportScroller } from '../../../../core/services';
import { AdminService } from '../../services/admin.service';
import { AdminOrganizationDetailResult, AdminOrgMember } from '../../models/admin-organization-detail.model';
import { NotificationService } from '../../../../core/services';
import { bindModalToQueryParam, ModalService } from '../../../../shared/modal';
import { AdminKeyCallsModal } from './key-calls-modal/admin-key-calls-modal';

export type OrgDetailTab = 'members' | 'keys' | 'calls';

const ORG_DETAIL_TABS: OrgDetailTab[] = ['members', 'keys', 'calls'];

/** `:slug` in the route (the list page always links by slug), but the
 *  contract accepts either the slug or the numeric id GET
 *  admin/organizations/:id doesn't care which arrives. */
@Component({
  selector: 'app-admin-organization-detail',
  standalone: false,
  templateUrl: './admin-organization-detail.html',
  styleUrl: './admin-organization-detail.scss',
})
export class AdminOrganizationDetail implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  slug!: string;
  detail: AdminOrganizationDetailResult | null = null;
  isLoading = true;
  /** Unknown org distinct from a transient load failure: the empty state
   *  reads differently and there's nothing to retry. */
  notFound = false;

  /** Mirrors `?tab=` so refresh and back/forward keep the open tab;
   *  Members is the default and leaves the param off. */
  activeTab: OrgDetailTab = 'members';
  /** Filters/sorts only the members this response returned, in the browser
   *  there's no server-side search on this route. */
  memberSearch = '';
  joinedSort: 'asc' | 'desc' = 'asc';

  private readonly modalService = inject(ModalService);

  /** An API key's calls open in a full modal bound to `?key=`, so refresh,
   *  Back/Forward and shared links all keep it open (see bindModalToQueryParam). */
  readonly keyCallsModal = bindModalToQueryParam({
    param: 'key',
    ready: () => !!this.detail,
    open: keyId => {
      const key = this.detail?.apiKeys.find(k => k.id === keyId);
      if (!key || !this.detail) return null;
      return this.modalService.full(AdminKeyCallsModal, {
        data: { key, orgName: this.detail.organization.name },
      });
    },
  });

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.slug = this.route.snapshot.paramMap.get('slug')!;
    this.route.queryParamMap
      .pipe(takeUntil(this._destroy$))
      .subscribe(params => {
        const tab = params.get('tab') as OrgDetailTab | null;
        this.activeTab = tab && ORG_DETAIL_TABS.includes(tab) ? tab : 'members';
      });
    this.load();
  }

  private load(): void {
    this.isLoading = true;
    this.notFound = false;
    this.adminService.getOrganization(this.slug)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          if (error?.status === 404) {
            this.notFound = true;
          } else {
            this.notificationService.error('Failed to load this organization. Please try again later.');
          }
          return of(null);
        }),
        finalize(() => { this.isLoading = false; }),
      )
      .subscribe(detail => {
        this.detail = detail;
        // A ?key= that was in the URL before the keys had loaded can open now.
        this.keyCallsModal.sync();
      });
  }

  private readonly scroller = inject(AppViewportScroller);

  /** Pushes a history entry (no replaceUrl, unlike the list pages' filters)
   *  so Back returns to the previous tab. */
  setTab(tab: OrgDetailTab): void {
    if (tab === this.activeTab) return;
    // The tabs are where you're looking: stay put instead of the router's
    // jump to the top of the page.
    this.scroller.overrideNextScroll(() => {});
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'members' ? null : tab, key: null },
      queryParamsHandling: 'merge',
    });
  }

  toggleJoinedSort(): void {
    this.joinedSort = this.joinedSort === 'asc' ? 'desc' : 'asc';
  }

  get visibleMembers(): AdminOrgMember[] {
    const members = this.detail?.members ?? [];
    const q = this.memberSearch.trim().toLowerCase();
    const filtered = q
      ? members.filter(m => `${m.user.name} ${m.user.email}`.toLowerCase().includes(q))
      : members;
    const dir = this.joinedSort === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => dir * (Date.parse(a.createdAt) - Date.parse(b.createdAt)));
  }

  initials(name: string | null | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    const first = parts[0][0];
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
